import * as React from "react";

import {
  Box,
  makeStyles,
  Theme,
  Typography,
  useTheme,
} from "@material-ui/core";
import * as Sentry from "@sentry/browser";
import { useQuery } from "react-query";

import {
  useBranding,
  useMerchantModel,
  useTranslation,
  useWindowFocus,
} from "client/hooks";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";
import { ScriptStatus } from "client/hooks/useScript";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { bootstrapMarqetaJS } from "client/lib/marqeta";
import { CardService } from "client/services";

interface IProps {
  activeLocationIndex: number;
  marqetaScriptStatus: ScriptStatus;
  setCardTapSuccessState: () => void;
}

const useStyles = makeStyles((theme: Theme) => {
  const { gray10 } = useNormalizedBrandPaletteValues(theme);

  return {
    card: {
      backgroundColor: gray10,
      borderRadius: theme.shape.borderRadius,
      color: theme.palette.common.white,
      height: 160,
      margin: theme.spacing(2, 0),
      padding: theme.spacing(0, 3, 3, 3),
      width: 230,
    },
    cardDetails: {
      fontWeight: "bold",
    },
    iframeWrapper: {
      display: "flex",
      "& > iframe": {
        height: 20,
        width: 40,
      },
    },
    iframeWrapperCard: {
      "& > iframe": {
        width: "100%",
      },
    },
    listWrapper: {
      margin: 0,
      padding: 0,
    },
    logo: {
      marginRight: theme.spacing(1),
    },
    errorBanner: {
      backgroundColor: "rgba(204, 51, 0, 0.05)", // one off to use opacity for current hex theme red color #CC3300
    },
  };
});

const POLLING_INTERVAL = 5000;
const MAX_POLLING_ERRORS = 15;

const DEFAULT_ERROR_MESSAGE = "Error fetching card information";

const inStoreMessagingBullets = [
  "uiInStoreSmallPurchase",
  "uiInStorePosPostalCode",
  "uiInStoreTransactionDeclinedEmail",
];

const ErrorBanner: React.FC<{ errorMsg: string }> = ({ errorMsg }) => {
  const classes = useStyles();
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      className={classes.errorBanner}
    >
      <Typography
        className={classes.cardDetails}
        variant="caption"
        color="error"
      >
        {errorMsg}
      </Typography>
    </Box>
  );
};

export const SelfServeActivationTabPanel: React.FC<IProps> = ({
  activeLocationIndex,
  marqetaScriptStatus,
  setCardTapSuccessState,
}) => {
  const {
    merchant: { locations },
  } = useMerchantModel();

  const classes = useStyles();

  const [marqetaJsError, setMarqetaJsError] = React.useState<null | string>(
    null
  );
  const [pollForActivation, setPollForActivation] = React.useState(false);
  const [
    selfServeFetchErrorCount,
    setSelfServeFetchErrorCount,
  ] = React.useState(0);
  const theme = useTheme();
  const { Logo } = useBranding();
  const t = useTranslation();

  const storeLocationId = locations[activeLocationIndex].label.value;

  const { data: hasDummyTappedData, isFetched: selfServeTapFetched } = useQuery<
    CardService.HasSelfServeActivated,
    Error
  >(
    [`has-self-service-activated`, storeLocationId],
    () => CardService.getHasSelfServeInstoreActivation(storeLocationId),
    {
      enabled: pollForActivation,
      refetchInterval: POLLING_INTERVAL,
      onSuccess: ({ hasSelfServeTransaction }) => {
        if (hasSelfServeTransaction) {
          setCardTapSuccessState();
          logAmplitudeEvent(
            "SELF_SERVE_INSTORE",
            "successfully_activated_terminal"
          );
        }
      },
      onError: (error) => {
        setSelfServeFetchErrorCount((prevErrorCount) => prevErrorCount + 1);
        if (selfServeFetchErrorCount < MAX_POLLING_ERRORS) {
          Sentry.captureException(error, {
            tags: {
              selfServeActivationError: true,
            },
          });
        }
      },
    }
  );

  useQuery<CardService.MarqetaAccessToken, Error>(
    [`get-marqeta-access-token`, storeLocationId],
    () => CardService.getMarqetaClientAccessToken(storeLocationId),
    {
      // we only fetch for the card if we've already for fetch for "completeness" of the tap and it's returned with no data or false
      enabled:
        selfServeTapFetched &&
        !hasDummyTappedData?.hasSelfServeTransaction &&
        marqetaScriptStatus === "ready",
      onSuccess: ({ accessToken }) => {
        bootstrapMarqetaJS({
          clientAccessToken: accessToken,
          onFailure: (_) => setMarqetaJsError(DEFAULT_ERROR_MESSAGE),
        });
        logAmplitudeEvent("SELF_SERVE_INSTORE", "virtual_cards_generated");
      },
      onError: (_) => {
        // Q: sentry error reporting?
        setMarqetaJsError(DEFAULT_ERROR_MESSAGE);
      },
    }
  );

  const windowFocused = useWindowFocus();

  React.useEffect(() => {
    // we initiate polling when the user has focus on both the window + the store card tab + not already fetched success tap
    setPollForActivation(
      Boolean(storeLocationId) &&
        windowFocused &&
        !hasDummyTappedData?.hasSelfServeTransaction &&
        selfServeFetchErrorCount < MAX_POLLING_ERRORS
    );

    return () => {
      // remove polling for this card if a user goes to another store card tab
      setPollForActivation(false);
    };
  }, [
    storeLocationId,
    windowFocused,
    hasDummyTappedData?.hasSelfServeTransaction,
    selfServeFetchErrorCount,
  ]);

  return (
    <>
      {marqetaJsError ? <ErrorBanner errorMsg={marqetaJsError} /> : null}
      <ul className={classes.listWrapper}>
        <Typography component="li" variant="caption">
          Enter the amount of <strong>$1.00</strong> in your card reader device.
        </Typography>
        <Typography component="li" variant="caption">
          Select <strong>Manual Credit Card Entry</strong>. Enter the following
          card information to complete the transaction.
        </Typography>
      </ul>
      <Box
        alignItems="center"
        className={classes.card}
        display="flex"
        flexDirection="column"
        id="marqeta-js-container"
        justifyContent="space-evenly"
      >
        <Box
          alignItems="center"
          display="flex"
          flex={2}
          justifyContent="flex-start"
          width="100%"
        >
          <Logo
            className={classes.logo}
            fill={theme.cash?.palette?.white ?? theme.bronte.palette.bondiMint}
            height={13}
            width={71}
          />
          <Typography variant="caption">{t("uiInStoreOneTimeUse")}</Typography>
        </Box>
        {marqetaJsError ? (
          <Box>
            <Typography
              className={classes.cardDetails}
              variant="caption"
              color="error"
            >
              {marqetaJsError}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              alignSelf="flex-start"
              className={classes.cardDetails}
              flex={1}
            >
              <div
                data-testid="mq-card-pan"
                className={`${classes.iframeWrapper} ${classes.iframeWrapperCard}`}
                id="mq-card-pan"
              ></div>
            </Box>
            <Box
              alignItems="center"
              className={classes.cardDetails}
              display="flex"
              justifyContent="space-between"
              width="100%"
            >
              <Typography variant="caption">
                <div className={classes.iframeWrapper} id="mq-card-exp"></div>
              </Typography>
              <Typography variant="caption">
                <div className={classes.iframeWrapper} id="mq-card-cvv"></div>
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <ul className={classes.listWrapper}>
        <Typography component="li" variant="caption">
          <strong>You will get a decline</strong> - this is{" "}
          <strong>correct</strong> as it is a test tap transaction.
        </Typography>
        {inStoreMessagingBullets.map((bulletString, index) => (
          <Typography
            key={`in-store-string-${index}`}
            component="li"
            variant="caption"
          >
            {t(bulletString)}
          </Typography>
        ))}
      </ul>
    </>
  );
};
